import { MouseEvent } from "react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import Card from "components/ui/card/Card";
import Tag from "components/ui/tag/Tag";
import { RELEVANCY_SEARCH_NEW_USERS, useVariableValue } from "lib/featureFlags";
import UIcon, { IconProps } from "lib/icons/untitled_ui/UIcon";
import { DataTypeName } from "lib/types/models";

type CardSize = "sm" | "lg";
export type PartType = "Strategy" | "Pipeline" | "Tenders" | "Bids" | "Documents";
type Content = {
  title: string;
  description: {
    sm: string;
    lg: string;
  };
  icon: IconProps["icon"];
  requiresDataType?: DataTypeName;
  showNewTag?: boolean;
};

function getCardContent(partType: PartType): Content {
  const cardContent: Record<PartType, Content> = {
    Strategy: {
      title: "Analyse the market",
      description: {
        sm: "Find top buyers and suppliers within your market",
        lg: "Analyse your market, find the right buyers to sell to and identify potential competitors or partner suppliers.",
      },
      icon: "strategyPart",
    },
    Pipeline: {
      title: "Find expiring contracts",
      description: {
        sm: "Create leads from expiring contracts to pre-engage",
        lg: "Find early buying signals, build your coverage and outreach to decision makers in minutes.",
      },
      icon: "pipelinePart",
    },
    Tenders: {
      title: "Find open tenders",
      description: {
        sm: "Find open tenders from every portal on the market",
        lg: "Build a centralised view of open tenders pulled from every portal on the market.",
      },
      icon: "tenderPart",
    },
    Bids: {
      title: "Qualify a tender",
      description: {
        sm: "Turn tender documents into a shareable report in minutes",
        lg: "Streamline your bid process, leveraging AI to qualify, collaborate and draft faster and more effectively.",
      },
      icon: "bidPart",
    },
    Documents: {
      title: "Find buyer documents",
      description: {
        sm: "Search buyer documents including budgets and strategy papers",
        lg: "Search buyer documents including budgets, meeting minutes, and strategy papers.",
      },
      icon: "box",
    },
  };

  return cardContent[partType];
}

function getRelevancyMiniIcon(partType: PartType): IconProps["icon"] {
  const miniIcons: Partial<Record<PartType, IconProps["icon"]>> = {
    Tenders: "file02",
    Bids: "feather",
    Pipeline: "file02",
    Documents: "box",
  };

  return miniIcons[partType] ?? getCardContent(partType).icon;
}

type Props = {
  partType: PartType;
  hasPaywall?: boolean;
  onClick?: (e: MouseEvent) => void;
  size?: CardSize;
  to?: string;
  search?: Record<string, unknown>;
};

export default function PartCard({
  partType,
  hasPaywall,
  onClick,
  to,
  search,
  size = "sm",
}: Props): JSX.Element {
  const isRelevancyNewUsers = useVariableValue(RELEVANCY_SEARCH_NEW_USERS, false);
  const { title, description, icon, showNewTag } = getCardContent(partType);

  if (isRelevancyNewUsers) {
    const miniIcon = getRelevancyMiniIcon(partType);

    return (
      <Card padding={12} height={42} onClick={onClick} to={to} search={search} fullWidth>
        <MiniContent alignItems="center" gap={8}>
          <UIcon icon={miniIcon} size={16} color="sysPrimaryDefault" />
          <MiniTitle>
            {title}
            {hasPaywall && <UIcon icon="lock01" size={14} />}
          </MiniTitle>
        </MiniContent>
      </Card>
    );
  }

  if (size === "lg") {
    return (
      <Card width={448} height={105} onClick={onClick} to={to} search={search}>
        <Flex justifyContent="space-between" alignItems="center" gap={16} height="100%">
          <UIcon icon={icon} size={40} />
          <Flex column gap={8} flexGrow={1}>
            <Title>
              {title}
              {hasPaywall && <UIcon icon={"lock01"} size={14} />}
            </Title>
            <Text description>{description[size]}</Text>
          </Flex>
        </Flex>
      </Card>
    );
  }

  return (
    <Card onClick={onClick} to={to} search={search}>
      <Title>
        <UIcon icon={icon} size={14} />
        {title}
        {showNewTag && <Tag label="NEW" variant="secondary" fontSize="sm" />}
        {hasPaywall && <UIcon icon={"lock01"} size={14} />}
      </Title>
      <Text>{description[size]}</Text>
    </Card>
  );
}

const MiniContent = styled(Flex)({
  height: "100%",
  minWidth: 0,
});

const MiniTitle = styled(Flex)(({ theme }) => ({
  alignItems: "center",
  flex: 1,
  fontSize: 14,
  fontWeight: 600,
  gap: 4,
  lineHeight: "125%",
  minWidth: 0,
  color: theme.colors.sysTextDefault,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const Title = styled(Flex)(({ theme }) => ({
  alignItems: "center",
  fontWeight: 600,
  gap: "8px",
  color: theme.colors.sysTextDefault,
}));
